export function MaximizingPropertyValue() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Strategies to Maximize Your Property's Value
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding valuation is just the first step. Smart property owners can take specific actions to increase their apartment building's value before selling, potentially adding tens or hundreds of thousands of dollars to the sale price.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/f3dce44dddf7fc0ee53f2d1ecd509cba.jpg"
              alt="Maximizing apartment building value through improvements"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">The Value-Add Approach</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Value-add strategies focus on increasing Net Operating Income (NOI), which directly increases property value. Since value equals NOI divided by cap rate, every dollar of additional NOI can add $10-$20 to your property's value depending on market cap rates.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The key is identifying improvements that generate returns exceeding their cost. A $50,000 renovation that increases NOI by $7,000 annually adds approximately $100,000 in value at a 7% cap rate—a 100% return on investment.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Value Creation Formula:</h4>
              <p className="text-gray-800 text-xl font-semibold mb-2">Value Increase = NOI Increase ÷ Cap Rate</p>
              <p className="text-gray-600">Example: $10,000 NOI increase ÷ 7% cap rate = $142,857 value increase</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">High-Impact Value Enhancement Strategies</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-lg border-t-4 border-green-600">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-arrow-up-line text-green-600 text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900">Increase Rental Income</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Raise rents to market rates:</strong> If rents are 10% below market, bringing them up can increase NOI by $20K-$50K+ annually</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Add income sources:</strong> Install coin laundry, charge for parking, add pet fees, storage rentals</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Reduce vacancy:</strong> Improve marketing, offer move-in specials, enhance curb appeal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Unit upgrades:</strong> Modern kitchens and baths can justify $100-$300/month rent increases</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-semibold text-green-800">ROI: 200-500% | Timeline: 3-12 months</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg border-t-4 border-blue-600">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-arrow-down-line text-blue-600 text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900">Reduce Operating Expenses</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Energy efficiency:</strong> LED lighting, programmable thermostats, low-flow fixtures reduce utility costs</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Property tax appeal:</strong> Challenge assessments if property is over-valued for tax purposes</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Insurance shopping:</strong> Get competitive quotes annually, bundle policies for discounts</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-blue-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Preventive maintenance:</strong> Regular maintenance prevents costly emergency repairs</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-semibold text-blue-800">ROI: 300-1000% | Timeline: Immediate-6 months</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg border-t-4 border-purple-600">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full">
                  <i className="ri-paint-brush-line text-purple-600 text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900">Cosmetic Improvements</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-purple-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Curb appeal:</strong> Fresh paint, landscaping, clean common areas create strong first impressions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-purple-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Common area upgrades:</strong> New flooring, lighting, and furniture in lobbies and hallways</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-purple-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Unit turnover improvements:</strong> Paint, flooring, fixtures in vacant units before showing</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-purple-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Signage and branding:</strong> Professional signage improves perceived property quality</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <p className="text-sm font-semibold text-purple-800">ROI: 100-300% | Timeline: 1-3 months</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg border-t-4 border-orange-600">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full">
                  <i className="ri-file-list-line text-orange-600 text-2xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900">Operational Improvements</h4>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Professional management:</strong> Efficient operations reduce expenses and improve tenant satisfaction</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Lease documentation:</strong> Ensure all leases are current, signed, and properly documented</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Financial records:</strong> Clean, organized books make property more attractive to buyers</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-orange-600 mt-1 flex-shrink-0"></i>
                  <span><strong>Tenant screening:</strong> Quality tenants reduce turnover and maintenance costs</span>
                </li>
              </ul>
              <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm font-semibold text-orange-800">ROI: 150-400% | Timeline: 3-6 months</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Real-World Value Enhancement Example</h3>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white/10 rounded-lg p-6">
                <h4 className="text-2xl font-bold mb-4">Before Improvements</h4>
                <div className="space-y-3 text-lg">
                  <p>Gross Rental Income: <strong>$240,000</strong></p>
                  <p>Vacancy Loss (15%): <strong>-$36,000</strong></p>
                  <p>Other Income: <strong>$0</strong></p>
                  <p className="border-t border-white/30 pt-2">Effective Gross Income: <strong>$204,000</strong></p>
                  <p>Operating Expenses: <strong>-$122,400</strong> (60%)</p>
                  <p className="border-t-2 border-white pt-3 text-xl">Net Operating Income: <strong>$81,600</strong></p>
                  <p className="text-2xl font-bold mt-4">Value at 8% cap: <strong>$1,020,000</strong></p>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6">
                <h4 className="text-2xl font-bold mb-4">After Improvements</h4>
                <div className="space-y-3 text-lg">
                  <p>Gross Rental Income: <strong>$276,000</strong> (+15%)</p>
                  <p>Vacancy Loss (7%): <strong>-$19,320</strong></p>
                  <p>Other Income: <strong>$12,000</strong> (laundry, parking)</p>
                  <p className="border-t border-white/30 pt-2">Effective Gross Income: <strong>$268,680</strong></p>
                  <p>Operating Expenses: <strong>-$134,340</strong> (50%)</p>
                  <p className="border-t-2 border-white pt-3 text-xl">Net Operating Income: <strong>$134,340</strong></p>
                  <p className="text-2xl font-bold mt-4">Value at 8% cap: <strong>$1,679,250</strong></p>
                </div>
              </div>
            </div>

            <div className="bg-white/20 rounded-lg p-6 text-center">
              <p className="text-2xl font-bold mb-2">Total Value Increase: $659,250</p>
              <p className="text-lg mb-4">Investment in improvements: $75,000</p>
              <p className="text-xl font-bold">Net Gain: $584,250 | ROI: 779%</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}